'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { useInView } from '@/hooks';
import { cn } from '@/lib/utils/tw-merge';
import { VEHICLE_CTA_SENTINEL_ID } from './vehicleDetail.constants';

/**
 * Below lg: the two inquiry buttons as a bar at the bottom edge, once the
 * aside's buttons have scrolled away above. It steps aside for the footer.
 * The buttons are rendered on the server and arrive as children.
 */
export function VehicleCtaBar({ children }: { children: ReactNode }) {
    const [pastActions, setPastActions] = useState(false);
    const footerInView = useInView('.site-footer');

    // A scroll listener, not an IntersectionObserver: a jump (anchor link,
    // restored scroll position) can carry the marker past the viewport without
    // ever crossing it, and the observer would never report.
    useEffect(() => {
        const sentinel = document.getElementById(VEHICLE_CTA_SENTINEL_ID);
        if (!sentinel) return;

        let frame = 0;
        const update = () => {
            frame = 0;
            setPastActions(sentinel.getBoundingClientRect().top < 0);
        };
        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(update);
        };

        update();
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);

        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
        };
    }, []);

    const visible = pastActions && !footerInView;

    return (
        <div className={cn('vehicle-cta-bar', visible && 'vehicle-cta-bar--visible')} inert={!visible}>
            {children}
        </div>
    );
}
